import React, { useState, useEffect } from "react";
import axios from "../../features/axiosHelper";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import "./Login.css";
import { useDispatch, useSelector } from "react-redux";
import { selectSignInState } from "../../features/auth/authSlice";

import {
  GoogleIcon,
  MicrosoftIcon,
} from "../../ImageRepo.js";

import {
  setRememberMe,
  selectRememberedUserName,
} from "../../features/auth/rememberMeSlice.js";
import { useTranslation } from "react-i18next";
import { identify as appcuesIdentify } from "@/features/appcuesHelper";

const UserCredentialsPane = ({
  setUserCredentialsPane,
  setForgotPasswordPane,
  setEnvironmentSelectionPane,
  setTenantGroup,
  Email,
  setEmail,
  setDarkLoadingBar,
  setRememberMeCheckBox,
  RememberMeCheckBox,
  Password,
  setPassword,
  setUserDescription,
  setTermsChallenge,
}) => {
  const { t } = useTranslation("login");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [EmailError, setEmailError] = useState(false);
  const [PasswordError, setPasswordError] = useState(false);
  const [CredentialsError, setCredentialsError] = useState(false);
  const [InactiveError, setInactiveError] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [TermsAgreement, setTermsAgreement] = useState(false);
  const [TermsUrl, setTermsUrl] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const userEmailInput = React.createRef();
  const [EnvironmentSelection, setEnvironmentSelection] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const rememberedUser = useSelector(selectRememberedUserName);

  const LogIn = async (email, password) => {
    setDarkLoadingBar(false);
    //params only used for resetting the password response
    var validationError = false;
    var isFromReset = email != null && password != null;
    if (Email === "") {
      setEmailError(true);
      validationError = true;
    }
    if (Password === "") {
      setPasswordError(true);
      validationError = true;
    }
    if (validationError && !isFromReset) return;
    setIsDisabled(true);

    const credentials = isFromReset
      ? { Email: email, Password: password }
      : { Email: Email, Password: Password };
    await axios
      .post("/login", credentials)
      .then(async (response) => {
        setIsResetPassword(false);

        localStorage.setItem("refreshToken", response.data.refreshToken);

        dispatch(
          setRememberMe({
            userName: RememberMeCheckBox ? email ?? Email : null,
          })
        );

        // Heap.io: Identify logged in user
        window.heap.identify(response.data.user.id);
        window.heap.addUserProperties({
          Username: response.data.user.username,
          Email: credentials.Email,
        });

        window.heap.track("Login - Success", {"Method": "Email and Password"});

        // Appcues: Identify logged in user
        // we need the same id or shared property for intigration between heap and appcues to work
        appcuesIdentify(response.data.user.id);
        setUserDescription(response.data.user.description);
        setTenantGroup(response.data.tenants);

        if (response.data.termsRequired && setTermsChallenge) {
          localStorage.setItem("pendingToken", response.data.pendingToken);
          setTermsChallenge({
            termsRequired: true,
            termsConditionId: response.data.termsConditionId,
            htmlContent: response.data.htmlContent,
          });
        } else if (setTermsChallenge) {
          setTermsChallenge(null);
        }

        setUserCredentialsPane(false);
        setForgotPasswordPane(false);
        setEnvironmentSelectionPane(true);
        setDarkLoadingBar(false);
      })
      .catch((err) => {
        setIsDisabled(() => false);
        if (err?.response.data === "NewPasswordNeeded") {
          alert(
            "Please click 'forgot password?' to reset your account password."
          );
        } else if (err?.response.data === "CredentialsInvalid") {
          setCredentialsError(true);
        } else if (err?.response.data === "Inactive") {
          setInactiveError(true);
        } else {
          console.error(err);
        }
      });
  };

  useEffect(() => {
    if (rememberedUser) {
      if (Email !== null) {
        return;
      }
      setEmail(rememberedUser);
      setRememberMeCheckBox(true);
      userEmailInput.current.value = rememberedUser;
    }
  }, [rememberedUser]);

  const handleEmailChange = (event) => {
    setEmailError(false);
    setCredentialsError(false);
    setInactiveError(false);
    setEmail(event.target.value);
  };
  const handlePasswordChange = (event) => {
    setPasswordError(false);
    setCredentialsError(false);
    setPassword(event.target.value);
  };
  const handlePasswordEnterKey = (event) => {
    if (event.keyCode == 13) {
      LogIn();
    }
  };
  const checkTerms = () => {
    axios
      .get("/login/terms")
      .then((response) => {
        setTermsUrl(response.data.url);
        setTermsAgreement(response.data.newTerms);
      })
      .catch((err) => console.log(err));
  };
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  return (
    <div>
      <FormControl sx={{ width: "100%" }}>
        <Typography
          sx={{
            color: "var(--default-light)",
            fontSize: "15px",
            fontWeight: 400,
            mb: 1,
          }}
        >
          Username
        </Typography>
        <TextField
          variant="outlined"
          inputRef={userEmailInput}
          id="userName-input"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "var(--dark-border)",
              height: "60px",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "var(--neutral-6)",
                borderWidth: "1px",
              },
              "&:hover": {
                backgroundColor: "var(--dark-border)",
                boxShadow: "0px -1px 8.2px rgba(204, 204, 204, 0.75)",
                "& fieldset": {
                  borderColor: "var(--neutral-6)",
                  borderWidth: "1px",
                },
              },
              "&.Mui-focused": {
                backgroundColor: "var(--dark-border)",
                "& fieldset": {
                  borderColor: "var(--neutral-6)",
                  borderWidth: "1px",
                },
              },
              "& #userName-input.MuiInputBase-input": {
                color: "var(--default-light)",
                padding: "14px",
                height: "100%",
                "&::selection": {
                  backgroundColor: "#555",
                  color: "var(--default-light)",
                },
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
                  WebkitTextFillColor: "var(--default-light)",
                  caretColor: "var(--default-light)",
                  borderRadius: "4px",
                  transition: "background-color 9999s ease-in-out 0s",
                },
                "&:-webkit-autofill:hover": {
                  WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
                  WebkitTextFillColor: "var(--default-light)",
                },
                "&:-webkit-autofill:focus": {
                  WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
                  WebkitTextFillColor: "var(--default-light)",
                },
              },
            },
            "&:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
              WebkitTextFillColor: "var(--default-light)",
              caretColor: "var(--default-light)",
              borderRadius: "4px",
              transition: "background-color 9999s ease-in-out 0s",
            },
            "&:-webkit-autofill:hover": {
              WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
              WebkitTextFillColor: "var(--default-light)",
            },
            "&:-webkit-autofill:focus": {
              WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
              WebkitTextFillColor: "var(--default-light)",
            },
          }}
          InputLabelProps={{
            sx: {
              color: "white",
              transform: "translate(14px, 14px) scale(1)",
              "&.Mui-focused": {
                color: "white",
              },
            },
          }}
          InputProps={{
            sx: {
              color: "white",
              height: "100%",
              "&::selection": {
                backgroundColor: "#555",
                color: "white",
              },
            },
          }}
          onChange={handleEmailChange}
          error={EmailError}
          helperText={EmailError ? "Please enter a valid Email address" : ""}
        />
      </FormControl>
      <br />
      <br />
      <FormControl sx={{ width: "100%" }}>
        <Typography
          sx={{
            color: "var(--default-light)",
            fontSize: "15px",
            fontWeight: 400,
            mb: 1,
          }}
        >
          Password
        </Typography>
        <TextField
          variant="outlined"
          id="password-input"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "var(--dark-border)",
              height: "60px",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "var(--neutral-6)",
                borderWidth: "1px",
              },
              "&:hover": {
                backgroundColor: "var(--dark-border)",
                boxShadow: "0px -1px 8.2px rgba(204, 204, 204, 0.75)",
                "& fieldset": {
                  borderColor: "var(--neutral-6)",
                  borderWidth: "1px",
                },
              },
              "&.Mui-focused": {
                backgroundColor: "var(--dark-border)",
                "& fieldset": {
                  borderColor: "var(--neutral-6)",
                  borderWidth: "1px",
                },
              },
              "& #password-input.MuiInputBase-input": {
                color: "var(--default-light)",
                padding: "14px",
                height: "100%",
                "&::selection": {
                  backgroundColor: "#555",
                  color: "white",
                },
                "&:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
                  WebkitTextFillColor: "var(--default-light)",
                  caretColor: "var(--default-light)",
                  borderRadius: "4px",
                  transition: "background-color 9999s ease-in-out 0s",
                },
                "&:-webkit-autofill:hover": {
                  WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
                  WebkitTextFillColor: "var(--default-light)",
                },
                "&:-webkit-autofill:focus": {
                  WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
                  WebkitTextFillColor: "var(--default-light)",
                },
              },
            },
            "&:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
              WebkitTextFillColor: "var(--default-light)",
              caretColor: "var(--default-light)",
              borderRadius: "4px",
              transition: "background-color 9999s ease-in-out 0s",
            },
            "&:-webkit-autofill:hover": {
              WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
              WebkitTextFillColor: "var(--default-light)",
            },
            "&:-webkit-autofill:focus": {
              WebkitBoxShadow: "0 0 0 1000px var(--background-color) inset",
              WebkitTextFillColor: "var(--default-light)",
            },
          }}
          type={showPassword ? "text" : "password"}
          error={PasswordError || CredentialsError || InactiveError}
          helperText={
            CredentialsError
              ? "Incorrect email or password"
              : PasswordError
              ? "Enter your password"
              : InactiveError
              ? t('login.user-inactive')
              : ""
          }
          onChange={handlePasswordChange}
          onKeyDown={handlePasswordEnterKey}
          InputProps={{
            sx: {
              color: "white",
              height: "100%",
              "&::selection": {
                backgroundColor: "#555",
                color: "white",
              },
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  id="showPassword"
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  sx={{
                    color: "white",
                    width: "15px",
                    height: "15px",
                    padding: "12px",
                    "& svg": {
                      width: "15px",
                      height: "15px",
                      fontSize: "15px",
                    },
                  }}
                >
                  {showPassword ? (
                    <Visibility fontSize="inherit" />
                  ) : (
                    <VisibilityOff fontSize="inherit" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
      <Button
        id="loginBtn"
        variant="contained"
        onClick={() => LogIn()}
        disabled={isDisabled}
        sx={{
          marginTop: "20px",
          width: "100%",
          height: "60px",
          backgroundColor: "var(--default-light)",
          color: "var(--text-primary)",
          fontFamily: "Montserrat",
          fontWeight: "medium",
          fontSize: "24px",
          borderRadius: "8px",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "var(--text-primary)",
            fontWeight: 600,
            color: "var(--default-light)",
            border: "1px solid var(--default-light)",
          },
          "&:disabled": {
            backgroundColor: "var(--default-light)",
            color: "rgba(12,12,12,0.5)",
          },
          boxShadow: "none",
          "&.MuiButton-root": {
            boxShadow: "none",
          },
        }}
      >
        Login
      </Button>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          mt: 2,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              id="remember-me-checkbox"
              checked={RememberMeCheckBox}
              onChange={() => setRememberMeCheckBox(!RememberMeCheckBox)}
              sx={{
                color: "var(--default-light) ",
                "&#remember-me-checkbox.Mui-checked": {
                  color: "var(--default-light) ",
                },
              }}
            />
          }
          label="Remember me"
          sx={{
            color: "var(--default-light)",
            fontSize: "15px",
            "&#remember-me-checkbox.MuiTypography-root": {
              fontSize: "15px",
              color: "var(--default-light) !important",
            },
            "& .MuiSvgIcon-root": {
              color: "var(--default-light)",
            },
            "&:hover #remember-me-checkbox + .MuiSvgIcon-root": {
              color: "var(--default-light) !important",
            },
          }}
        />
        <Typography
          id="forgot-password-text"
          sx={{
            color: "var(--text-adornment) ",
            fontSize: "15px",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
              color: "var(--default-light)",
            },
          }}
          onClick={() => {
            setForgotPasswordPane(true);
            setEnvironmentSelection(false);
            setUserCredentialsPane(false);
          }}
        >
          Forgot your password?
        </Typography>
      </Box>
      <div className="externalLoginSection">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            my: 3,
            color: "white",
          }}
        >
          <Divider
            sx={{
              flexGrow: 1,
              backgroundColor: "var(--neutral-6)",
              height: "1px",
            }}
          />
          <Typography
            variant="body1"
            sx={{
              px: 2,
              fontSize: "14px",
              color: "var(--grey-lowlight)",
            }}
          >
            Or Login With
          </Typography>
          <Divider
            sx={{
              flexGrow: 1,
              backgroundColor: "var(--neutral-6)",
              height: "1px",
            }}
          />
        </Box>
      </div>{" "}
      <form
        method="GET"
        action={"/api/login/users/external"}
        className="externalLoginSection"
      >
        <div className="externalLoginButtonsWrapper">
          <Button
            className="externalLoginButton btn-secondary"
            type="submit"
            name="provider"
            value="Google"
          >
            <img src={GoogleIcon} alt="Google Icon" />
            Sign In
          </Button>
          <Button
            className="externalLoginButton btn-secondary"
            type="submit"
            name="provider"
            value="Microsoft"
          >
            <img src={MicrosoftIcon} alt="Google Icon" />
            Sign In
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UserCredentialsPane;
